/**
 * 모던 버튼 컴포넌트
 * 다양한 variant, size, 상태를 지원하는 재사용 가능한 버튼
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, spacing, borderRadius, shadows, animations, components } from '../../styles/designTokens';

// ===== 타입 정의 =====
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';
export type ButtonState = 'default' | 'loading' | 'disabled';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    state?: ButtonState;
    fullWidth?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    children: React.ReactNode;
}

// ===== 스타일드 컴포넌트 =====
const ButtonBase = styled.button<{
    $variant: ButtonVariant;
    $size: ButtonSize;
    $state: ButtonState;
    $fullWidth: boolean;
}>`
  /* 기본 스타일 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-family: ${typography.fontFamily.sans};
  font-weight: ${typography.fontWeight.medium};
  text-decoration: none;
  border: 1px solid transparent;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  position: relative;
  overflow: hidden;
  
    /* 크기별 스타일 */
  ${({ $size }) => {
        switch ($size) {
            case 'sm':
                return css`
          height: ${components.button.height.sm};
          padding: 0.5rem 0.75rem;
          font-size: ${typography.fontSize.sm};
          border-radius: ${borderRadius.md};
          
          @media (max-width: 480px) {
            height: 36px;
            padding: 0.5rem 0.75rem;
            font-size: ${typography.fontSize.xs};
          }
        `;
            case 'md':
                return css`
          height: ${components.button.height.md};
          padding: 0.75rem 1rem;
          font-size: ${typography.fontSize.sm};
          
          @media (max-width: 480px) {
            height: 40px;
            padding: 0.5rem 1rem;
            font-size: ${typography.fontSize.sm};
          }
        `;
            case 'lg':
                return css`
          height: ${components.button.height.lg};
          padding: 1rem 1.5rem;
          font-size: ${typography.fontSize.base};
          
          @media (max-width: 480px) {
            height: 44px;
            padding: 0.75rem 1.25rem;
            font-size: ${typography.fontSize.sm};
          }
        `;
            case 'xl':
                return css`
          height: ${components.button.height.xl};
          padding: 1.25rem 2rem;
          font-size: ${typography.fontSize.lg};
          
          @media (max-width: 480px) {
            height: 48px;
            padding: 1rem 1.5rem;
            font-size: ${typography.fontSize.base};
          }
        `;
        }
    }}
  
  /* 전체 너비 */
  ${({ $fullWidth }) => $fullWidth && css`
    width: 100%;
  `}
  
  /* Variant별 스타일 */
  ${({ $variant, $state }) => {
        if ($state === 'disabled') {
            return css`
        background-color: ${colors.semantic.background.tertiary};
        color: ${colors.semantic.text.disabled};
        border-color: ${colors.semantic.border.primary};
        cursor: not-allowed;
        opacity: 0.6;
      `;
        }

        if ($state === 'loading') {
            return css`
        cursor: wait;
        opacity: 0.8;
      `;
        }

        switch ($variant) {
            case 'primary':
                return css`
          background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
          color: ${colors.semantic.text.inverse};
          border-color: ${colors.primary[500]};
          box-shadow: ${shadows.primary};
          
          &:hover {
            background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%);
            transform: translateY(-1px);
            box-shadow: 0 8px 25px rgba(59, 130, 246, 0.25);
          }
          
          &:active {
            transform: translateY(0);
            box-shadow: ${shadows.md};
          }
        `;

            case 'secondary':
                return css`
          background-color: ${colors.semantic.background.primary};
          color: ${colors.semantic.text.primary};
          border-color: ${colors.semantic.border.primary};
          box-shadow: ${shadows.sm};
          
          &:hover {
            background-color: ${colors.semantic.background.tertiary};
            border-color: ${colors.semantic.border.secondary};
            transform: translateY(-1px);
            box-shadow: ${shadows.md};
          }
          
          &:active {
            transform: translateY(0);
            box-shadow: ${shadows.sm};
          }
        `;

            case 'outline':
                return css`
          background-color: transparent;
          color: ${colors.primary[600]};
          border-color: ${colors.primary[300]};
          
          &:hover {
            background-color: ${colors.primary[50]};
            border-color: ${colors.primary[400]};
            color: ${colors.primary[700]};
          }
          
          &:active {
            background-color: ${colors.primary[100]};
          }
        `;

            case 'ghost':
                return css`
          background-color: transparent;
          color: ${colors.semantic.text.secondary};
          border-color: transparent;
          
          &:hover {
            background-color: ${colors.semantic.background.tertiary};
            color: ${colors.semantic.text.primary};
          }
          
          &:active {
            background-color: ${colors.semantic.background.secondary};
          }
        `;

            case 'danger':
                return css`
          background: linear-gradient(135deg, ${colors.error[500]} 0%, ${colors.error[600]} 100%);
          color: ${colors.semantic.text.inverse};
          border-color: ${colors.error[500]};
          box-shadow: ${shadows.error};
          
          &:hover {
            background: linear-gradient(135deg, ${colors.error[600]} 0%, ${colors.error[700]} 100%);
            transform: translateY(-1px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.25);
          }
          
          &:active {
            transform: translateY(0);
            box-shadow: ${shadows.md};
          }
        `;

            case 'success':
                return css`
          background: linear-gradient(135deg, ${colors.success[500]} 0%, ${colors.success[600]} 100%);
          color: ${colors.semantic.text.inverse};
          border-color: ${colors.success[500]};
          box-shadow: ${shadows.success};
          
          &:hover {
            background: linear-gradient(135deg, ${colors.success[600]} 0%, ${colors.success[700]} 100%);
            transform: translateY(-1px);
            box-shadow: 0 8px 25px rgba(34, 197, 94, 0.25);
          }
          
          &:active {
            transform: translateY(0);
            box-shadow: ${shadows.md};
          }
        `;
        }
    }}
  
  /* 포커스 스타일 */
  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
  
  /* 로딩 상태 */
  ${({ $state }) => $state === 'loading' && css`
    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top-color: currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  `}
  
  /* 아이콘 스타일 */
  svg {
    width: 1em;
    height: 1em;
    flex-shrink: 0;
  }
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
  }
`;

// ===== 로딩 스피너 컴포넌트 =====
const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

// ===== 메인 컴포넌트 =====
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = 'primary',
            size = 'md',
            state = 'default',
            fullWidth = false,
            leftIcon,
            rightIcon,
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        const isDisabled = disabled || state === 'disabled';
        const isLoading = state === 'loading';

        return (
            <ButtonBase
                ref={ref}
                $variant={variant}
                $size={size}
                $state={isDisabled ? 'disabled' : isLoading ? 'loading' : 'default'}
                $fullWidth={fullWidth}
                disabled={isDisabled || isLoading}
                {...props}
            >
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        {leftIcon}
                        {children}
                        {rightIcon}
                    </>
                )}
            </ButtonBase>
        );
    }
);

Button.displayName = 'Button';

// ===== 버튼 그룹 컴포넌트 =====
export const ButtonGroup = styled.div<{ $orientation?: 'horizontal' | 'vertical' }>`
  display: flex;
  gap: ${spacing[1]};
  
  ${({ $orientation = 'horizontal' }) =>
        $orientation === 'vertical'
            ? css`
          flex-direction: column;
          align-items: stretch;
        `
            : css`
          flex-direction: row;
          align-items: center;
        `
    }
  
  /* 버튼 그룹 내 버튼 스타일 조정 */
  ${ButtonBase} {
    &:not(:last-child) {
      ${({ $orientation = 'horizontal' }) =>
        $orientation === 'horizontal'
            ? css`margin-right: 0;`
            : css`margin-bottom: 0;`
    }
    }
  }
`;

// ===== 기본 내보내기 =====
export default Button;
